import { encounterCreatureField, itemsCol, layerFlagOn } from '../../shared/array-types.js';
import { passiveEntryActive } from '../actions/spell-components.js';
import { carrierEntryDefinition } from './carrier-entry.js';

type Row = Record<string, unknown>;
export interface MovementStartReaders {
  value: (row: Row, key: string) => unknown;
  entries: (row: Row, key: string) => unknown[];
  statuses: (row: Row) => Row[];
  definition: (id: string) => Row | null;
  name?: (row: Row) => string;
  inCombat: (encounter: Row, row: Row) => boolean;
}
export const MOVEMENT_ACTION_CARRIERS = ['passive_ability', 'reaction_ability', 'free_ability', 'menta', 'curse',
  'at_will_spell', 'innate_spell_per_long_rest', 'spells_known', 'opportunity_attack_ability'];
export const NOT_YOUR_TURN_MESSAGE = 'You cannot move when it is not your turn!';
const record = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const upper = (value: unknown): string => String(value ?? '').trim().toUpperCase();

/** The compiler elides zero speeds. An uncompiled/unknown speed stays unknown for the commit scope to diagnose. */
export function ruleSpeed(caster: Row, read: MovementStartReaders, speed: string): number {
  const raw = read.value(caster, speed);
  return raw === undefined && record(record(caster.computed)?.creature) ? 0 : Number(raw);
}

export function zeroSpeedMessage(caster: Row, read: MovementStartReaders, speed: string): string {
  const kind = speed === 'teleport' ? 'teleport' : speed.replace(/^speed_/, '');
  const verb = kind === 'flying' ? 'fly' : kind === 'jumping' ? 'jump' : kind === 'climbing' ? 'climb'
    : kind === 'burrowing' ? 'burrow' : kind === 'teleport' ? 'teleport' : 'move';
  const cluster = record(record(record(record(caster.computed)?.creature)?.speeds)?.[kind]);
  const sources = cluster?.locked && Array.isArray(cluster.sources) ? cluster.sources.map(record) : [];
  const held = new Set(read.statuses(caster).map(status => upper(status.status_item_id)));
  const lock = sources.find(source => source && 'value_brace' in source && Number(source.value_brace) === 0 && held.has(upper(source.href_id)));
  const name = lock ? String(itemsCol(read.definition(upper(lock.href_id)), 'item_name') ?? '').trim() : '';
  return name ? `You cannot ${verb} while ${name}.` : `You cannot ${verb} — your ${kind} speed is 0.`;
}

/** Ordinary self-movement only. Callers keep authority checks and bypass this rule for externally moved bodies.
 * Reads the held row and its existing readers; never hydrates, compiles, clones or emits telemetry. */
export function ordinaryMovementStartRefusal(caster: Row, read: MovementStartReaders, context: {
  encounter?: Row; actorId?: string; speed?: string; allowOffTurn?: boolean;
} = {}): string | null {
  const creature = read.name?.(caster) || String(record(caster.player_characters)?.player_name
    ?? encounterCreatureField(caster, 'encounter_creature_name') ?? itemsCol(caster, 'item_name') ?? 'This creature');
  const blockingName = (entry: unknown): string | null => {
    const { definition } = carrierEntryDefinition(entry, read.definition);
    return definition && layerFlagOn(read.value(definition, 'no_actions'))
      ? String(itemsCol(definition, 'item_name') ?? '').trim() || 'unable to take actions' : null;
  };
  // Prefer the first active held status even if the compiled row also carries the aggregate flag.
  for (const status of read.statuses(caster)) {
    const name = blockingName(status);
    if (name) return `${creature} cannot move while ${name}.`;
  }
  for (const carrier of MOVEMENT_ACTION_CARRIERS) {
    for (const entry of read.entries(caster, carrier)) {
      if (carrier === 'passive_ability' && typeof entry !== 'string' && !passiveEntryActive(entry)) continue;
      const name = blockingName(entry);
      if (name) return `${creature} cannot move while ${name}.`;
    }
  }
  if (layerFlagOn(read.value(caster, 'no_actions'))) return `${creature} cannot move while unable to take actions.`;
  if (!context.allowOffTurn && context.encounter && read.inCombat(context.encounter, caster)
    && upper(read.value(context.encounter, 'current_encounter_creature_id_turn')) !== upper(context.actorId)) return NOT_YOUR_TURN_MESSAGE;
  if (context.speed) {
    const speed = ruleSpeed(caster, read, context.speed);
    if (Number.isFinite(speed) && speed <= 0) return zeroSpeedMessage(caster, read, context.speed);
  }
  return null;
}
